/* eslint-disable react/style-prop-object */
import React from 'react'
import { css } from '@emotion/css'
import { Chip, fontSize, spacing, colors } from '@offerzen/pinata-shame'
import BlocksIcon from '@offerzen/zenicon/BlocksIcon'
import BriefcaseIcon from '@offerzen/zenicon/BriefcaseIcon'
import BuildingIcon from '@offerzen/zenicon/BuildingIcon'
import ComputerIcon from '@offerzen/zenicon/ComputerIcon'
import ConversationIcon from '@offerzen/zenicon/ConversationIcon'
import GearIcon from '@offerzen/zenicon/GearIcon'
import PeopleIcon from '@offerzen/zenicon/PeopleIcon'
import PieChartIcon from '@offerzen/zenicon/PieChartIcon'
import QuestionCircleIcon from '@offerzen/zenicon/QuestionCircleIcon'
import RocketIcon from '@offerzen/zenicon/RocketIcon'
import SignOutIcon from '@offerzen/zenicon/SignOutIcon'
import StarIcon from '@offerzen/zenicon/StarIcon'
import UsersIcon from '@offerzen/zenicon/UsersIcon'
import { companyStateMap } from './companyHelpers'
import CandidatesNavItemLabel from '../components/CandidatesNavItemLabel'

const defaultDesktopDropdownItems = {
  analytics: true,
  profile: true,
  teammates: true,
  settings: true,
  billing: true,
  integration_partners: true,
  email_preferences: true,
  manage_data: true,
  privacy_centre: true,
  help: true,
  log_out: true,
}

const limitedDesktopDropdownItems = {
  analytics: false,
  profile: true,
  teammates: true,
  settings: true,
  billing: true,
  integration_partners: true,
  email_preferences: true,
  manage_data: true,
  privacy_centre: true,
  help: true,
  log_out: true,
}

const defaultMobileAdminItems = {
  analytics: true,
  profile: true,
  teammates: true,
  account: true,
  privacy_centre: true,
  help: true,
  log_out: true,
}

const limitedMobileAdminItems = {
  analytics: false,
  profile: true,
  teammates: true,
  account: true,
  privacy_centre: true,
  help: true,
  log_out: true,
}

export const liveCompanyNavigation = Object.freeze({
  desktopDropdownItems: defaultDesktopDropdownItems,
  mobileAdminItems: defaultMobileAdminItems,
  navItems: {
    dashboard: true,
    welcome: false,
    positions: true,
    candidates: true,
    shortlist: true,
    interview_requests: true,
  },
})

export const dormantCompanyNavigation = Object.freeze({
  desktopDropdownItems: defaultDesktopDropdownItems,
  mobileAdminItems: defaultMobileAdminItems,
  navItems: {
    dashboard: false,
    welcome: false,
    positions: true,
    candidates: true,
    shortlist: false,
    interview_requests: true,
  },
})

export const preLiveCompanyNavigation = Object.freeze({
  desktopDropdownItems: {
    analytics: false,
    profile: false,
    teammates: true,
    settings: true,
    billing: true,
    integration_partners: true,
    email_preferences: true,
    manage_data: true,
    privacy_centre: true,
    help: true,
    log_out: true,
  },
  mobileAdminItems: limitedMobileAdminItems,
  navItems: {
    dashboard: false,
    welcome: true,
    candidates: false,
    shortlist: false,
    interview_requests: false,
    profile: true,
    positions: true,
  },
})

export const defaultCompanyNavigation = Object.freeze({
  desktopDropdownItems: limitedDesktopDropdownItems,
  mobileAdminItems: limitedMobileAdminItems,
  navItems: {
    dashboard: false,
    welcome: false,
    positions: false,
    candidates: true,
    shortlist: false,
    interview_requests: false,
  },
})

export const companyStateNavigation = ({ companyState }) => {
  switch (companyState) {
    case 'live':
      return liveCompanyNavigation

    case 'dormant':
      return dormantCompanyNavigation

    // All company pre-live states have the same navigation
    case 'onboarding':
    case 'onboarding_completed':
    case 'setup_call':
    case 'setup_meeting':
    case 'call_scheduled':
    case 'completing_profile':
    case 'ready_to_go_live':
    case 'accepting_terms':
      return preLiveCompanyNavigation

    case 'rejected':
    case 'junk_data':
    case 'unresponsive':
    default:
      return defaultCompanyNavigation
  }
}

const isArchivedIRs = (location, companySlug) =>
  location.pathname === `/company/${companySlug}/interview_requests` && location.search.includes('archived=true')

const isOpenIRs = (location, companySlug) =>
  location.pathname === `/company/${companySlug}/interview_requests` && !location.search.includes('archived=true')

const isOnPositions = (location, companySlug, status) =>
  location.pathname === `/company/${companySlug}/positions` && location.search.includes(`status=${status}`)

export const navigationLinks = ({
  companySlug = '',
  impersonatedHiringTeam = '',
  emailPreferencesAccessTokenKey = '',
  unreadInterviewRequestCount = '',
  companyState = '',
}) => {
  const companyLive = companyState === 'live'
  const stateLabel = companyLive ? companyStateMap[companyState].label : 'Hidden'

  return {
    welcome: {
      label: 'Welcome',
      path: 'welcome',
      to: `/company/${companySlug}/welcome${impersonatedHiringTeam}`,
      icon: RocketIcon,
    },
    profile: {
      label: 'Profile',
      path: 'profile',
      href: `/companies/${companySlug}/profile`,
      icon: BuildingIcon,
      mobileLabel: () => (
        <>
          <span>Profile</span>
          <If condition={!!stateLabel}>
            <Chip
              theme={companyLive ? 'success' : 'error'}
              style="dark"
              label={stateLabel}
              className={css({
                marginLeft: 'auto',
              })}
            />
          </If>
        </>
      ),
    },
    dashboard: {
      label: 'Dashboard',
      path: 'dashboard',
      to: `/company/${companySlug}/dashboard${impersonatedHiringTeam}`,
      icon: BlocksIcon,
    },
    positions: {
      label: 'Positions',
      path: 'positions',
      to: `/company/${companySlug}/positions${impersonatedHiringTeam}`,
      icon: BriefcaseIcon,

      subItems: [
        {
          value: 'open_positions',
          label: 'Open',
          path: 'positions?status=open',
          to: `/company/${companySlug}/positions${`${impersonatedHiringTeam ? `${impersonatedHiringTeam}&` : '?'}status=open`}`,
          isActive: (_, location) => isOnPositions(location, companySlug, 'open'),
        },
        {
          value: 'closed_positions',
          label: 'Closed',
          path: 'positions?status=closed',
          to: `/company/${companySlug}/positions${`${impersonatedHiringTeam ? `${impersonatedHiringTeam}&` : '?'}status=closed`}`,
          isActive: (_, location) => isOnPositions(location, companySlug, 'closed'),
        },
      ],
    },
    candidates: {
      desktopLabel: CandidatesNavItemLabel,
      mobileLabel: CandidatesNavItemLabel,
      path: 'candidates',
      to: `/company/${companySlug}/candidates${impersonatedHiringTeam}`,
      icon: PeopleIcon,
      isActive: (_, location) => {
        return (
          location.pathname.includes('/candidates') ||
          location.pathname.includes('/applications') ||
          location.pathname.includes('/recommendations')
        )
      },
    },
    shortlist: {
      label: 'Favourites',
      path: 'shortlist',
      to: `/company/${companySlug}/shortlist${impersonatedHiringTeam}`,
      icon: StarIcon,
    },
    interview_requests: {
      label: 'Messages',
      mobileLabel: () => (
        <>
          <span>Messages</span>

          <If condition={Number(unreadInterviewRequestCount) > 0}>
            <Chip
              style="dark"
              label={unreadInterviewRequestCount}
              className={css({
                marginLeft: 'auto',
                backgroundColor: colors.whiteLilac,
                color: colors.amethyst,
              })}
            />
          </If>
        </>
      ),
      desktopLabel: () => (
        <>
          Messages
          <If condition={Number(unreadInterviewRequestCount) > 0}>
            <span
              className={css({
                borderRadius: '50%',
                backgroundColor: colors.amethyst,
                color: colors.white,
                fontSize: fontSize.small,
                width: 20,
                height: 20,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginLeft: spacing.small,
              })}>
              {unreadInterviewRequestCount}
            </span>
          </If>
        </>
      ),
      path: 'interview_requests',
      to: `/company/${companySlug}/interview_requests${impersonatedHiringTeam}`,
      icon: ConversationIcon,
      restrictUserRole: 'reviewer',

      subItems: [
        {
          value: 'open_interview_requests',
          label: 'Open',
          path: 'interview_requests',
          to: `/company/${companySlug}/interview_requests${impersonatedHiringTeam}`,
          isActive: (_, location) => isOpenIRs(location, companySlug),
        },
        {
          value: 'archived_interview_requests',
          label: 'Closed',
          path: 'interview_requests?status=archived',
          to: `/company/${companySlug}/interview_requests${`${impersonatedHiringTeam ? `${impersonatedHiringTeam}&` : '?'}archived=true`}`,
          isActive: (_, location) => isArchivedIRs(location, companySlug),
        },
      ],
    },
    analytics: {
      label: 'Analytics',
      path: 'analytics',
      to: `/company/${companySlug}/analytics${impersonatedHiringTeam}`,
      icon: PieChartIcon,
    },
    teammates: {
      label: 'Team',
      path: 'team',
      href: `/company/${companySlug}/teammates`,
      icon: UsersIcon,
    },
    account: {
      label: 'Account',
      path: 'settings',
      href: `/company/${companySlug}/settings`,
      icon: GearIcon,
      subItems: ['settings', 'billing', 'integration_partners', 'manage_data', 'email_preferences'],
    },
    settings: {
      label: 'Settings',
      href: `/company/${companySlug}/settings`,
      path: 'settings',
    },
    billing: {
      label: 'Billing details',
      path: 'billing',
      href: `/company/${companySlug}/billing`,
      restrictUserRole: 'reviewer',
    },
    integration_partners: {
      label: 'Integrations',
      path: 'integration_partners',
      to: `/company/${companySlug}/integration_partners`,
      restrictUserRole: 'reviewer',
    },
    manage_data: {
      label: 'Manage your data',
      path: 'manage_data',
      href: `/company/${companySlug}/manage_data`,
    },
    email_preferences: {
      label: 'Email preferences',
      path: 'email_preferences',
      href: `/email_preferences/${emailPreferencesAccessTokenKey}`,
    },
    privacy_centre: {
      label: 'Privacy centre',
      path: 'privacy-centre',
      href: '/privacy-centre',
      target: '_blank',
      icon: ComputerIcon,
    },
    help: {
      label: 'Help',
      path: 'help',
      href: `/company/${companySlug}/help`,
      target: '_blank',
      icon: QuestionCircleIcon,
    },
    // Special case because it's not a link
    log_out: {
      label: 'Log out',
      path: null,
      icon: SignOutIcon,
    },
  }
}
